import type { AppUpdate } from 'src/types/app-updates/app-update';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMegaphone, faSquareArrowUpRight } from '@fortawesome/pro-regular-svg-icons';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { useLikeToggle } from 'src/hooks/app-updates/use-like-toggle';

import { getLocalizedText } from 'src/utils/multilingual';

import { useTranslate } from 'src/locales';

import { toast } from 'src/components/snackbar';
import { AnimatedLikeButton } from 'src/components/animate/animated-like-button';

type AppUpdateDetailViewProps = {
  update: AppUpdate;
};

export function AppUpdateDetailView({ update }: AppUpdateDetailViewProps) {
  const { t, currentLang } = useTranslate('app-updates');
  const lang = currentLang.value;

  const { liked, likeCount, isLiking, toggle } = useLikeToggle({
    updateId: update.id,
    initialLiked: update.liked,
    initialLikeCount: update.likeCount,
    onError: () => toast.error(t('errors.like')),
  });

  return (
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FontAwesomeIcon icon={faMegaphone} size="lg" />
        <Typography variant="subtitle1">{getLocalizedText(update.title, lang)}</Typography>
      </Box>

      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {getLocalizedText(update.body, lang)}
      </Typography>

      {update.linkUrl && getLocalizedText(update.linkUrl, lang) && (
        <Button
          component="a"
          variant="outlined"
          size="small"
          href={getLocalizedText(update.linkUrl, lang)}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<FontAwesomeIcon icon={faSquareArrowUpRight} />}
          sx={{ alignSelf: 'flex-start' }}
        >
          {getLocalizedText(update.linkLabel, lang) || t('learnMore')}
        </Button>
      )}

      <Stack direction="row" alignItems="center" spacing={0.5}>
        <AnimatedLikeButton liked={liked} isLiking={isLiking} onClick={toggle} size={20} />
        {likeCount > 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('likedBy', { count: likeCount })}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}
